import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Button } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
    CalendarTodayOutlined, PeopleOutlined, ConfirmationNumberOutlined,
    ArrowForwardOutlined, LocalFireDepartmentOutlined, EmojiEventsOutlined,
} from '@mui/icons-material';

const B = {
    deep:    '#0D0820',
    dark:    '#1A0F3D',
    mid:     '#2D1B69',
    purple:  '#7B5EA7',
    light:   '#9B7EC8',
    lavender:'#F7F4FC',
    ink:     '#0D0A1A',
    green:   '#00C896',
    greenDim:'#00C89618',
    red:     '#FF4D6A',
    orange:  '#FF9500',
    gold:    '#F5C842',
};

const REWARDS = [
    {
        name:        'Daily Check-In',
        description: 'Claim your reward every day and build your streak to unlock bigger bonuses.',
        icon:        CalendarTodayOutlined,
        link:        '/rewards/daily-checkin',
        accent:      '#007AFF',
        gradient:    'linear-gradient(145deg, #003A8C 0%, #0055CC 60%, #007AFF 100%)',
        glow:        '#007AFF',
        badge:       '🔥 3-Day Streak',
        stat:        '₦50/day',
        statLabel:   'Today\'s reward',
    },
    {
        name:        'Refer & Earn',
        description: 'Invite friends to NG Credit and earn ₦500 for every friend who signs up.',
        icon:        PeopleOutlined,
        link:        '/refer',
        accent:      '#FF2D55',
        gradient:    'linear-gradient(145deg, #7A0020 0%, #CC1F44 60%, #FF2D55 100%)',
        glow:        '#FF2D55',
        badge:       '💰 2 Referrals',
        stat:        '₦500/friend',
        statLabel:   'Per referral',
    },
    {
        name:        'Raffle Draw',
        description: 'Earn raffle tickets by saving and improving your credit. More tickets = more chances.',
        icon:        ConfirmationNumberOutlined,
        link:        '/raffle',
        accent:      B.gold,
        gradient:    'linear-gradient(145deg, #7A4800 0%, #CC7A00 60%, #F5C842 100%)',
        glow:        B.gold,
        badge:       '🎟️ 12 Tickets',
        stat:        '31 Dec',
        statLabel:   'Next draw',
    },
];

const RewardsGrid: React.FC = () => {
    const theme   = useTheme();
    const isDark  = theme.palette.mode === 'dark';
    const navigate = useNavigate();

    const pageBg    = isDark ? B.deep : B.lavender;
    const textPri   = isDark ? '#fff' : B.ink;
    const textFaint = alpha(isDark ? '#fff' : B.ink, .28);

    return (
        <Box sx={{ minHeight: '100vh', background: pageBg, pb: 10, fontFamily: "'DM Sans', system-ui, sans-serif" }}>

            {/* ══ HERO ══ */}
            <Box sx={{
                background: `linear-gradient(160deg, ${B.deep} 0%, ${B.mid} 55%, ${B.purple} 100%)`,
                mt: { xs: '-76px', sm: 0 },
                pt: { xs: '80px', sm: '28px' },
                pb: { xs: 4, sm: 14 }, px: 3, position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -70, right: -50, width: 240, height: 240, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.light, .18)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', bottom: -10, left: -40, width: 200, height: 200, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.gold, .08)} 0%, transparent 70%)`, pointerEvents: 'none' }} />

                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                        <Typography sx={{ color: alpha('#fff', .35), fontSize: '.62rem', letterSpacing: '.18em', textTransform: 'uppercase', mb: .5 }}>
                            NG Credit
                        </Typography>
                        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.5rem', letterSpacing: '-.03em', fontFamily: "'Instrument Serif', serif" }}>
                            Rewards
                        </Typography>
                        <Typography sx={{ color: alpha('#fff', .45), fontSize: '.8rem', mt: .5 }}>
                            Earn while you save and grow
                        </Typography>
                    </Box>
                    <Box sx={{ width: 48, height: 48, borderRadius: '14px', background: alpha(B.gold, .15), border: `1px solid ${alpha(B.gold, .3)}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <EmojiEventsOutlined sx={{ color: B.gold, fontSize: 24 }} />
                    </Box>
                </Box>
            </Box>

            <Container maxWidth="sm" sx={{ mt: { xs: 2, sm: -8 }, position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>

                {/* ── Total earnings strip ── */}
                <Box sx={{
                    borderRadius: '20px', p: 2.5, mb: 3,
                    background: isDark ? alpha(B.mid, .55) : '#fff',
                    border: `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}`,
                    boxShadow: `0 4px 28px ${alpha(B.dark, isDark ? .25 : .06)}`,
                    backdropFilter: isDark ? 'blur(20px)' : 'none',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                    <Box>
                        <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', fontWeight: 600, mb: .5 }}>Total Earned</Typography>
                        <Typography sx={{ color: textPri, fontFamily: "'Instrument Serif', serif", fontSize: '1.6rem', fontWeight: 700, letterSpacing: '-.008em' }}>₦1,500</Typography>
                        <Typography sx={{ color: alpha(B.green, .9), fontSize: '.72rem', fontWeight: 600 }}>+₦50 today</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        {[
                            { label: 'Streak', value: '3🔥' },
                            { label: 'Tickets', value: '12🎟️' },
                        ].map(s => (
                            <Box key={s.label} sx={{ textAlign: 'center' }}>
                                <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.95rem' }}>{s.value}</Typography>
                                <Typography sx={{ color: textFaint, fontSize: '.62rem', fontWeight: 600, letterSpacing: '.05em' }}>{s.label.toUpperCase()}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* ── Reward cards ── */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {REWARDS.map(r => {
                        const Icon = r.icon;
                        return (
                            <Box key={r.name} onClick={() => navigate(r.link)} sx={{
                                borderRadius: '24px', overflow: 'hidden', cursor: 'pointer',
                                position: 'relative',
                                background: r.gradient,
                                boxShadow: `0 12px 40px ${alpha(r.glow, .3)}`,
                                transition: 'transform .22s cubic-bezier(.16,1,.3,1), box-shadow .22s',
                                '&:hover': { transform: 'translateY(-4px)', boxShadow: `0 20px 50px ${alpha(r.glow, .45)}` },
                            }}>
                                {/* glow orb */}
                                <Box sx={{ position: 'absolute', top: -40, right: -40, width: 180, height: 180, borderRadius: '50%', background: `radial-gradient(circle, ${alpha('#fff', .12)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
                                <Box sx={{ position: 'absolute', bottom: -20, left: -20, width: 120, height: 120, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(r.glow, .2)} 0%, transparent 70%)`, pointerEvents: 'none' }} />

                                <Box sx={{ p: 2.5, position: 'relative', zIndex: 1 }}>
                                    <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Box sx={{ width: 48, height: 48, borderRadius: '14px', background: alpha('#fff', .15), display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(10px)' }}>
                                                <Icon sx={{ color: '#fff', fontSize: 24 }} />
                                            </Box>
                                            <Box>
                                                <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1rem', letterSpacing: '-.02em' }}>{r.name}</Typography>
                                                <Box sx={{ display: 'inline-flex', alignItems: 'center', background: alpha('#fff', .15), borderRadius: 20, px: 1.25, py: .3, mt: .4 }}>
                                                    <Typography sx={{ color: '#fff', fontSize: '.65rem', fontWeight: 700 }}>{r.badge}</Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                        <ArrowForwardOutlined sx={{ color: alpha('#fff', .5), fontSize: 18, mt: .5 }} />
                                    </Box>

                                    <Typography sx={{ color: alpha('#fff', .75), fontSize: '.78rem', lineHeight: 1.5, mb: 2.5 }}>
                                        {r.description}
                                    </Typography>

                                    {/* stat + CTA row */}
                                    <Box display="flex" alignItems="center" justifyContent="space-between">
                                        <Box sx={{ background: alpha('#000', .2), borderRadius: '12px', px: 1.75, py: 1, backdropFilter: 'blur(10px)' }}>
                                            <Typography sx={{ color: alpha('#fff', .55), fontSize: '.6rem', fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase' }}>{r.statLabel}</Typography>
                                            <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '.95rem', fontFamily: "'Instrument Serif', serif" }}>{r.stat}</Typography>
                                        </Box>
                                        <Button variant="contained"
                                                sx={{ background: alpha('#fff', .18), color: '#fff', backdropFilter: 'blur(10px)', borderRadius: 50, px: 2.5, py: .9, textTransform: 'none', fontWeight: 700, fontSize: '.8rem', boxShadow: 'none', '&:hover': { background: alpha('#fff', .28), boxShadow: 'none' } }}>
                                            {r.name === 'Daily Check-In' ? 'Claim' : r.name === 'Refer & Earn' ? 'Invite' : 'Enter Draw'}
                                        </Button>
                                    </Box>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>

                {/* footer */}
                <Box sx={{ mt: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: .75 }}>
                    <Typography sx={{ fontSize: '.62rem', color: textFaint, letterSpacing: '.05em' }}>
                        REWARDS RESET DAILY AT MIDNIGHT WAT
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default RewardsGrid;